<script setup lang="ts" generic="T extends string | number">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

interface Opt { value: T; label: string; disabled?: boolean }

const props = withDefaults(
  defineProps<{
    modelValue: T;
    options: Opt[];
    variant?: 'accent' | 'dark';
    align?: 'left' | 'right';
    disabled?: boolean;
    ariaLabel?: string;
  }>(),
  { variant: 'accent', align: 'left', disabled: false, ariaLabel: '' },
);
const emit = defineEmits<{ (e: 'update:modelValue', v: T): void }>();

const open = ref(false);
const highlight = ref(0);
const triggerEl = ref<HTMLElement | null>(null);
const panelEl = ref<HTMLElement | null>(null);
// Panel is teleported to <body> and positioned `fixed` from the trigger's rect,
// so it escapes every parent stacking context / overflow clip (catalog cards).
const panelStyle = ref<Record<string, string>>({});

const selectedLabel = computed(() => props.options.find((o) => o.value === props.modelValue)?.label ?? '');

function updatePosition() {
  const el = triggerEl.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  const style: Record<string, string> = {
    position: 'fixed',
    top: `${Math.round(r.bottom + 6)}px`,
    minWidth: `${Math.round(r.width)}px`,
  };
  if (props.align === 'right') style.right = `${Math.round(window.innerWidth - r.right)}px`;
  else style.left = `${Math.round(r.left)}px`;
  panelStyle.value = style;
}

function openPanel() {
  if (props.disabled) return;
  const idx = props.options.findIndex((o) => o.value === props.modelValue);
  highlight.value = idx >= 0 ? idx : 0;
  updatePosition();
  open.value = true;
  window.addEventListener('scroll', updatePosition, true);
  window.addEventListener('resize', updatePosition);
  scrollToHighlight();
}
function close() {
  open.value = false;
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
}
function toggle() { open.value ? close() : openPanel(); }

function choose(o: Opt) {
  if (o.disabled) return;
  emit('update:modelValue', o.value);
  close();
}

function move(dir: number) {
  const n = props.options.length;
  if (!n) return;
  let i = highlight.value;
  for (let step = 0; step < n; step++) {
    i = (i + dir + n) % n;
    if (!props.options[i].disabled) break;
  }
  highlight.value = i;
  scrollToHighlight();
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) {
    if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); openPanel(); }
    return;
  }
  if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
  else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
  else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const o = props.options[highlight.value]; if (o) choose(o); }
  else if (e.key === 'Escape') { e.preventDefault(); close(); }
  else if (e.key === 'Tab') { close(); }
}

function scrollToHighlight() {
  nextTick(() => {
    (panelEl.value?.children[highlight.value] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
  });
}

function onDocPointer(e: MouseEvent) {
  const target = e.target as Node;
  if (triggerEl.value?.contains(target) || panelEl.value?.contains(target)) return;
  close();
}
onMounted(() => document.addEventListener('mousedown', onDocPointer));
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocPointer);
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
});
</script>

<template>
  <div class="sm-root">
    <button
      ref="triggerEl"
      type="button"
      class="sm-trigger"
      :class="variant === 'dark' ? 'sm-dark' : 'sm-accent'"
      :disabled="disabled"
      :aria-label="ariaLabel"
      :aria-expanded="open"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span class="sm-label">{{ selectedLabel }}</span>
      <svg class="sm-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
        <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    </button>

    <Teleport to="body">
      <ul v-if="open" ref="panelEl" class="sm-panel" :style="panelStyle" @keydown="onKeydown">
        <li
          v-for="(o, idx) in options"
          :key="`${o.value}`"
          class="sm-option"
          :class="[idx === highlight ? 'is-active' : '', o.disabled ? 'is-disabled' : '']"
          @click="choose(o)"
          @mouseenter="highlight = idx"
        >
          {{ o.label }}
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<style scoped>
.sm-root {
  position: relative;
  display: inline-block;
}
.sm-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border-radius: 9999px;
  padding: 0.5rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.sm-trigger:focus-visible {
  outline: none;
  border-color: var(--color-accent-2);
}
.sm-trigger:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.sm-accent {
  background-color: #000;
  border: 1px solid var(--color-accent);
}
.sm-dark {
  background-color: var(--color-surface-2);
  border: 1px solid var(--color-line);
}
.sm-chevron {
  color: var(--color-accent);
  flex-shrink: 0;
}
</style>

<style>
/* Unscoped: the panel is teleported to <body>, so scoped selectors wouldn't reach it. */
.sm-panel {
  z-index: 1000;
  max-height: 16rem;
  overflow-y: auto;
  padding: 0.25rem;
  border-radius: 0.75rem;
  border: 1px solid var(--color-line);
  background: var(--color-surface);
  box-shadow: 0 20px 50px -12px rgba(0, 0, 0, 0.7);
}
.sm-panel .sm-option {
  padding: 0.5rem 0.7rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  color: var(--color-ink);
  white-space: nowrap;
  cursor: pointer;
}
.sm-panel .sm-option.is-active {
  background: rgba(240, 86, 26, 0.15);
  color: #fff;
}
.sm-panel .sm-option.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
